// src/pages/CompanyManagement/tabs/UsersTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const UsersTab = ({ companyId, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API_URL}/api/companies/${companyId}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      enqueueSnackbar(
        error.response?.data?.error || t('CompanyManagement.error_load_users'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search)
    );
  });

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      manager: 'warning',
      user: 'info',
      viewer: 'default'
    };
    return colors[role] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('CompanyManagement.users_title')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.users_count', { count: filteredUsers.length })}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('CompanyManagement.search_users')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          {filteredUsers.length === 0 ? (
            <Alert severity="info">
              {searchTerm ? t('CompanyManagement.no_users_search') : t('CompanyManagement.no_users')}
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('CompanyManagement.user_name')}</TableCell>
                    <TableCell>{t('CompanyManagement.user_email')}</TableCell>
                    <TableCell>{t('CompanyManagement.user_role')}</TableCell>
                    <TableCell>{t('CompanyManagement.user_status')}</TableCell>
                    <TableCell align="right">{t('Common.permissions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {user.firstName} {user.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          color={getRoleColor(user.role)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.active ? <ActiveIcon /> : <InactiveIcon />}
                          label={user.active ? t('CompanyManagement.active') : t('CompanyManagement.inactive')}
                          size="small"
                          color={user.active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={t('CompanyManagement.user_permissions_count', { count: user.permissionCount || 0 })}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('CompanyManagement.users_summary', {
                total: users.length,
                active: users.filter(u => u.active).length,
                inactive: users.filter(u => !u.active).length
              })}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsersTab;
